// pages/GolfStatisticsDashboard.js
import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import api from "../api/axiosInstance";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const GolfStatisticsDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [insights, setInsights] = useState(null);
  const [timeRange, setTimeRange] = useState(6); // months
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState({
    summary: true,
    trends: true,
    insights: true,
  });

  const navigate = useNavigate();

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

  useEffect(() => {
    fetchSummary();
    fetchTrends(timeRange);
    fetchInsights();
  }, [timeRange]);

  const fetchSummary = async () => {
    try {
      setLoading((prev) => ({ ...prev, summary: true }));
      const response = await api.get("/golf-statistics/my-summary");
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching summary:", error);
      toast.error("Failed to load statistics summary");
    } finally {
      setLoading((prev) => ({ ...prev, summary: false }));
    }
  };

  const fetchTrends = async (months) => {
    try {
      setLoading((prev) => ({ ...prev, trends: true }));
      const response = await api.get(
        `/golf-statistics/historical-trends/${months}`
      );
      setTrends(response.data);
    } catch (error) {
      console.error("Error fetching trends:", error);
      toast.error("Failed to load trend data");
    } finally {
      setLoading((prev) => ({ ...prev, trends: false }));
    }
  };

  const fetchInsights = async () => {
    try {
      setLoading((prev) => ({ ...prev, insights: true }));
      const response = await api.get("/golf-statistics/performance-insights");
      setInsights(response.data);
    } catch (error) {
      console.error("Error fetching insights:", error);
      toast.error("Failed to load performance insights");
    } finally {
      setLoading((prev) => ({ ...prev, insights: false }));
    }
  };

  // Prepare trend data for charts
  const prepareTrendData = () => {
    if (!trends) return [];

    const months = Object.keys(trends.scoreTrend || {});
    return months.map((month) => ({
      name: month.substring(0, 3), // Short month name
      score: trends.scoreTrend[month] || 0,
      handicap: trends.handicapTrend[month] || 0,
      fairways: trends.fairwaysTrend[month] || 0,
      gir: trends.girTrend[month] || 0,
      putts: trends.puttingTrend[month] || 0,
    }));
  };

  // Prepare performance metrics data for bar chart
  const preparePerformanceData = () => {
    if (!trends) return [];

    const months = Object.keys(trends.scoreTrend || {});
    return months.map((month) => ({
      name: month.substring(0, 3),
      fairways: trends.fairwaysTrend[month] || 0,
      gir: trends.girTrend[month] || 0,
      putts: trends.puttingTrend[month] || 0,
    }));
  };

  if (loading.summary) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading golf statistics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex justify-between items-center py-4">
            <div>
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center text-gray-600 hover:text-gray-900 mb-2"
              >
                <svg
                  className="w-3 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to Dashboard
              </button>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Golf Statistics
              </h1>
              {/* <p className="text-sm sm:text-base text-gray-600">
                Track and analyze your golf performance
              </p> */}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-1 sm:mb-2">
              Average Score
            </h3>
            <p className="text-2xl sm:text-3xl font-bold text-blue-600">
              {summary?.averages?.scoring || "N/A"}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-1 sm:mb-2">
              Handicap Index
            </h3>
            <p className="text-2xl sm:text-3xl font-bold text-green-600">
              {summary?.averages?.handicap || "N/A"}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-1 sm:mb-2">
              Total Rounds
            </h3>
            <p className="text-2xl sm:text-3xl font-bold text-purple-600">
              {summary?.totalRounds || 0}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <h3 className="text-sm sm:text-base font-semibold text-gray-700 mb-1 sm:mb-2">
              Latest Round
            </h3>
            <p className="text-xl sm:text-2xl font-bold text-orange-600">
              {summary?.latestRound?.score || "N/A"}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 truncate">
              {summary?.latestRound?.course}
            </p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-4 sm:mb-6">
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex space-x-4 sm:space-x-8 min-w-max">
              {["overview", "trends", "insights"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm capitalize whitespace-nowrap ${
                    activeTab === tab
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          {activeTab === "overview" && (
            <OverviewTab summary={summary} loading={loading} COLORS={COLORS} />
          )}
          {activeTab === "trends" && (
            <TrendsTab
              trends={trends}
              timeRange={timeRange}
              setTimeRange={setTimeRange}
              loading={loading}
              COLORS={COLORS}
              prepareTrendData={prepareTrendData}
              preparePerformanceData={preparePerformanceData}
            />
          )}
          {activeTab === "insights" && (
            <InsightsTab
              insights={insights}
              loading={loading}
              COLORS={COLORS}
            />
          )}
        </div>
      </main>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ summary, loading, COLORS }) => {
  if (loading.summary) {
    return <div className="text-center py-6 sm:py-8">Loading overview...</div>;
  }

  if (!summary || summary.message) {
    return (
      <div className="text-center py-6 sm:py-8">
        <p className="text-gray-500 text-sm sm:text-base">
          No statistics available. Start by adding your first round!
        </p>
      </div>
    );
  }

  const scoringDistribution =
    summary.recentRounds && summary.recentRounds[0]
      ? [
          { name: "Eagles", value: summary.recentRounds[0].eagles || 0 },
          { name: "Birdies", value: summary.recentRounds[0].birdies || 0 },
          { name: "Pars", value: summary.recentRounds[0].pars || 0 },
          { name: "Bogeys", value: summary.recentRounds[0].bogeys || 0 },
          {
            name: "Double Bogeys+",
            value: summary.recentRounds[0].doubleBogeys || 0,
          },
        ]
      : [];

  // Prepare data for putting performance chart
  const puttingData = summary.recentRounds
    ? summary.recentRounds.map((round, index) => ({
        name: `R${index + 1}`,
        putts: round.totalPutts || 0,
        score: round.totalScore || 0,
      }))
    : [];

  return (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
        Performance Overview
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Scoring Distribution Pie Chart */}
        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
            Scoring Distribution
          </h3>
          {scoringDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={scoringDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {scoringDistribution.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} shots`, "Count"]} />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-6 sm:py-8 text-sm">
              No scoring data available
            </p>
          )}
        </div>

        {/* Putting Performance Bar Chart */}
        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
            Putting Performance
          </h3>
          {puttingData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={puttingData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <Bar dataKey="putts" fill="#8884D8" name="Putts" />
                <Bar dataKey="score" fill="#FF8042" name="Score" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-6 sm:py-8 text-sm">
              No putting data available
            </p>
          )}
        </div>
      </div>

      {/* Recent Rounds */}
      <div className="mt-4 sm:mt-6">
        <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
          Recent Rounds
        </h3>
        {summary.recentRounds && summary.recentRounds.length > 0 ? (
          <div className="space-y-2 sm:space-y-3">
            {summary.recentRounds.map((round, index) => (
              <div
                key={index}
                className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-3 sm:p-4 bg-gray-50 rounded-lg border space-y-2 sm:space-y-0"
              >
                <div className="flex-1">
                  <p className="font-semibold text-base sm:text-lg truncate">
                    {round.courseName}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500">
                    {round.roundDate}
                  </p>
                </div>
                <div className="text-left sm:text-right">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600">
                    {round.totalScore}
                  </p>
                  <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600 mt-1">
                    <span>Hcp: {round.handicapIndex || "N/A"}</span>
                    <span>Putts: {round.totalPutts || "N/A"}</span>
                    {round.fairwaysHit && round.fairwaysPossible && (
                      <span>
                        FW:{" "}
                        {(
                          (round.fairwaysHit / round.fairwaysPossible) *
                          100
                        ).toFixed(0)}
                        %
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-6 sm:py-8 text-sm">
            No recent rounds
          </p>
        )}
      </div>
    </div>
  );
};

// Trends Tab Component
const TrendsTab = ({
  trends,
  timeRange,
  setTimeRange,
  loading,
  COLORS,
  prepareTrendData,
  preparePerformanceData,
}) => {
  const trendData = prepareTrendData();
  const performanceData = preparePerformanceData();

  if (loading.trends) {
    return <div className="text-center py-6 sm:py-8">Loading trends...</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
          Performance Trends
        </h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(parseInt(e.target.value))}
          className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
        >
          <option value={3}>Last 3 Months</option>
          <option value={6}>Last 6 Months</option>
          <option value={12}>Last 12 Months</option>
        </select>
      </div>

      {trendData.length > 0 ? (
        <div className="space-y-6 sm:space-y-8">
          {/* Score and Handicap Trends */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Score Trend */}
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
                Score Trend
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke={COLORS[0]}
                    strokeWidth={2}
                    name="Avg Score"
                    dot={{ fill: COLORS[0], strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, fill: COLORS[0] }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Handicap Trend */}
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
                Handicap Trend
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Line
                    type="monotone"
                    dataKey="handicap"
                    stroke={COLORS[1]}
                    strokeWidth={2}
                    name="Handicap"
                    dot={{ fill: COLORS[1], strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5, fill: COLORS[1] }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Performance Metrics Bar Chart */}
          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
            <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
              Performance Metrics
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip formatter={(value) => [`${value}%`, "Percentage"]} />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <Bar
                  dataKey="fairways"
                  fill={COLORS[2]}
                  name="Fairways %"
                  radius={[2, 2, 0, 0]}
                />
                <Bar
                  dataKey="gir"
                  fill={COLORS[3]}
                  name="GIR %"
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Putting Trend */}
          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
            <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
              Putting Trend
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                <Line
                  type="monotone"
                  dataKey="putts"
                  stroke={COLORS[4]}
                  strokeWidth={2}
                  name="Avg Putts"
                  dot={{ fill: COLORS[4], strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5, fill: COLORS[4] }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="text-center py-6 sm:py-8">
          <p className="text-gray-500 text-sm sm:text-base">
            No trend data available for the selected period
          </p>
        </div>
      )}
    </div>
  );
};

// Insights Tab Component
const InsightsTab = ({ insights, loading, COLORS }) => {
  if (loading.insights) {
    return <div className="text-center py-6 sm:py-8">Loading insights...</div>;
  }

  if (!insights || insights.message) {
    return (
      <div className="text-center py-6 sm:py-8">
        <p className="text-gray-500 text-sm sm:text-base">
          No insights available. Add more rounds to get performance insights.
        </p>
      </div>
    );
  }

  // Prepare data for strengths/weaknesses chart
  const strengthsData = insights.strengthsWeaknesses
    ? Object.entries(insights.strengthsWeaknesses).map(
        ([category, rating]) => ({
          category:
            category.length > 10 ? category.substring(0, 10) + "..." : category,
          rating: rating === "Excellent" ? 100 : rating === "Good" ? 75 : 50,
          color:
            rating === "Excellent"
              ? COLORS[1]
              : rating === "Good"
              ? COLORS[2]
              : COLORS[3],
        })
      )
    : [];

  return (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
        Performance Insights
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Best & Worst Rounds */}
        <div className="space-y-3 sm:space-y-4">
          <h3 className="text-base sm:text-lg font-semibold">Round Analysis</h3>

          {insights.bestRound && (
            <div className="bg-green-50 p-3 sm:p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800 text-sm sm:text-base">
                Best Round
              </h4>
              <p className="text-xl sm:text-2xl font-bold text-green-600">
                {insights.bestRound.score}
              </p>
              <p className="text-xs sm:text-sm text-green-700 truncate">
                {insights.bestRound.course}
              </p>
              <p className="text-xs text-green-600">
                {insights.bestRound.date}
              </p>
            </div>
          )}

          {insights.worstRound && (
            <div className="bg-red-50 p-3 sm:p-4 rounded-lg border border-red-200">
              <h4 className="font-semibold text-red-800 text-sm sm:text-base">
                Worst Round
              </h4>
              <p className="text-xl sm:text-2xl font-bold text-red-600">
                {insights.worstRound.score}
              </p>
              <p className="text-xs sm:text-sm text-red-700 truncate">
                {insights.worstRound.course}
              </p>
              <p className="text-xs text-red-600">{insights.worstRound.date}</p>
            </div>
          )}
        </div>

        {/* Consistency */}
        {insights.consistency && (
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-base sm:text-lg font-semibold">
              Consistency Analysis
            </h3>
            <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200">
              <div className="flex justify-between mb-2 text-sm sm:text-base">
                <span className="text-blue-700">Average Score:</span>
                <span className="font-semibold">
                  {insights.consistency.averageScore}
                </span>
              </div>
              <div className="flex justify-between mb-2 text-sm sm:text-base">
                <span className="text-blue-700">Std Dev:</span>
                <span className="font-semibold">
                  {insights.consistency.standardDeviation}
                </span>
              </div>
              <div className="flex justify-between text-sm sm:text-base">
                <span className="text-blue-700">Rating:</span>
                <span
                  className={`font-semibold ${
                    insights.consistency.consistencyRating === "Excellent"
                      ? "text-green-600"
                      : insights.consistency.consistencyRating === "Good"
                      ? "text-yellow-600"
                      : "text-red-600"
                  }`}
                >
                  {insights.consistency.consistencyRating}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Strengths & Weaknesses Chart */}
      {strengthsData.length > 0 && (
        <div className="mt-4 sm:mt-6">
          <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">
            Strengths & Weaknesses
          </h3>
          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={strengthsData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} fontSize={12} />
                <YAxis
                  type="category"
                  dataKey="category"
                  fontSize={12}
                  width={60}
                />
                <Tooltip formatter={(value) => [`${value}%`, "Rating"]} />
                <Bar dataKey="rating" name="Performance Rating">
                  {strengthsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default GolfStatisticsDashboard;
